import type { ReactElement } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useUIDSeed } from 'react-uid';
import { monthDayAndYear } from 'utils/dateTimes';
import { capitalize } from 'utils/strings';

import { get } from 'utils/network';
import { ApiRoute, Route } from 'config';

import useAsyncEffect from 'hooks/useAsyncEffect';
import useCurrentUser from 'hooks/useCurrentUser';

import type Giveaway from 'models/Giveaway';
import type { IGiveaways } from 'models/Giveaways';
import Giveaways from 'models/Giveaways';

import { Component as DeliverIcon } from 'icons/icon-mktg--deliver-shadow.svg';
import { Component as BoxIcon } from 'icons/icon-mktg--deliver.svg';
import { Component as EmailIcon } from 'icons/icon-mktg--email-shadow.svg';
import { Component as SuccessIcon } from 'icons/icon-mktg--check-shadow.svg';
import { Component as CaretIcon } from 'icons/icon-ui--caret-outline.svg';

import Banner from 'components/shared/Banner';
import GiveawayForm from './GiveawayForm';

const statusName = (giveaway: Giveaway): string => {
  if (giveaway.beenRedeemed) return 'Claimed';

  if (giveaway.status === 'expired') return 'Expired';

  return 'Not claimed yet';
};

type NotEnoughOrdersProps = {
  ordersToNextGiveaway: number;
};

function NotEnoughOrders({ ordersToNextGiveaway }: NotEnoughOrdersProps): ReactElement {
  return (
    <>
      <p className="u-text--md u-text--lg-lg">
        You&apos;re just &nbsp;
        {ordersToNextGiveaway}
        &nbsp;
        {ordersToNextGiveaway === 1 ? 'delivery' : 'deliveries'}
        &nbsp; away from unlocking a
        <strong> free box </strong>
        to give a friend who hasn&apos;t tried Purple Carrot.
      </p>
      <p className="u-text--md u-text--lg-lg">
        p.s. No need to keep track, when it&apos;s time to share a
        box with a friend, we&apos;ll let you know.Warning: the hard
        part is deciding who to send it to.
        <br />
        Share-a-box credits are subject to expiration if not used within one-year.
      </p>
    </>
  );
}

function GiveawayPage(): ReactElement {
  const seed = useUIDSeed();
  const { currentUser } = useCurrentUser();
  const bannerRef = useRef<HTMLDivElement>();

  // @ts-expect-error TODO: fix null safety
  const [giveaways, setGiveaways] = useState<Giveaway[]>(null);
  const [remainingGiveawaysCount, setRemainingGiveawaysCount] = useState(0);
  const [redeemedGiveawaysCount, setRedeemedGiveawaysCount] = useState(0);
  const [submittedGiveawaysCount, setSubmittedGiveawaysCount] = useState(0);
  // @ts-expect-error TODO: fix null safety
  const [expiredGiveaway, setExpiredGiveaway] = useState<Giveaway>(null);
  const [showGiveawayList, setShowGiveawayList] = useState(false);
  const [sentGiveaways, setSentGiveaways] = useState<Giveaway[]>([]);
  const [giveawayToSend, setGiveawayToSend] = useState<Giveaway>();
  const [ordersToNextGiveaway, setOrdersToNextGiveaway] = useState(0);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [giftUserEmail, setGiftUserEmail] = useState('');

  useEffect(() => {
    if (showSuccessBanner || showInfoBanner) {
      bannerRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [showSuccessBanner, showInfoBanner]);

  const statusOptions = [
    {
      id: 'all',
      label: 'All',
    },
    {
      id: 'claimed',
      label: 'Claimed',
    },
    {
      id: 'not_claimed',
      label: 'Not Claimed Yet',
    },
    {
      id: 'expired',
      label: 'Expired',
    },
  ];

  const filterGiveaways = (filterStatus) => {
    switch (filterStatus) {
      case 'claimed':
        setSentGiveaways(giveaways.filter(
          (giveaway) => giveaway.status === 'redeemed'
            || giveaway.status === 'consumed',
        ));
        break;

      case 'expired':
        setSentGiveaways(
          giveaways.filter((giveaway) => giveaway.status === 'expired'),
        );
        break;

      case 'not_claimed':
        setSentGiveaways(
          giveaways.filter((giveaway) => giveaway.status === 'sent'),
        );
        break;

      default:
        setSentGiveaways(
          giveaways.filter((giveaway) => giveaway.status !== 'earned'),
        );
    }
  };

  const getGiveaways = async () => {
    const response = await get<IGiveaways>(ApiRoute.GIVEAWAYS);

    if (response.ok) {
      // @ts-expect-error TODO: fix null safety
      const giveawaysResp = new Giveaways(response.parsedBody);
      setGiveaways(giveawaysResp.giveaways);
      setRemainingGiveawaysCount(giveawaysResp.remaining_giveaways_count);
      setRedeemedGiveawaysCount(giveawaysResp.redeemed_giveaways_count);
      setOrdersToNextGiveaway(giveawaysResp.orders_to_next_giveaway);

      setSubmittedGiveawaysCount(() => giveawaysResp.giveaways?.filter(
        (giveaway) => giveaway.sent_at !== null,
      ).length);

      setExpiredGiveaway(() => giveawaysResp.giveaways?.filter(
        (giveaway) => giveaway.status === 'expired',
      )[0] ?? null);

      setSentGiveaways(
        giveawaysResp.giveaways?.filter(
          (giveaway) => giveaway.status !== 'earned',
        ),
      );

      setGiveawayToSend(
        giveawaysResp.giveaways?.filter(
          (giveaway) => giveaway.status === 'earned',
        )[0] ?? null,
      );
    }
  };

  const handleShowInfoBanner = () => {
    setShowSuccessBanner(false);
    setShowInfoBanner(true);
  };

  const handleShowSuccessBanner = (email) => {
    setGiftUserEmail(email);
    getGiveaways();
    setShowInfoBanner(false);
    setShowSuccessBanner(true);
  };

  useAsyncEffect(async (isMounted) => {
    if (isMounted.current) {
      getGiveaways();
    }
  }, []);

  // sorting sent gievaways chronologically
  const sortedSentGiveaways = sentGiveaways
    .sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());

  if (currentUser.isEmpty || giveaways === null) {
    return <div className="col-lg-10" />;
  }

  if (currentUser.isDeactivated) {
    return (
      <div className="col-lg-10">
        <div className="c-no-giveaways">
          <div className="c-no-giveaways__content">
            <BoxIcon />
            <h1 className="c-heading c-heading--brand-4">
              Spread a Little Plant-Based Love
            </h1>
            <p className="u-text--md u-text--lg-lg">
              <strong>Your account is not currently active.</strong>
              <br />
              Reactivate your account to be eligible to send a free box.
            </p>
            <a href={Route.PROFILE} className="c-button c-button--primary">
              Reactivate Now
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (giveaways.length === 0) {
    return (
      <div className="col-lg-10">
        <div className="c-no-giveaways">
          <div className="c-no-giveaways__content">
            <BoxIcon />
            <h1 className="c-heading c-heading--brand-4">
              Spread a Little Plant-Based Love
            </h1>
            <NotEnoughOrders ordersToNextGiveaway={ordersToNextGiveaway} />
          </div>
        </div>
      </div>
    );
  }

  let content;
  if (expiredGiveaway !== null) {
    content = (
      <p className="u-text--md u-text--lg-lg">
        The invite sent to&nbsp;
        <strong>
          {expiredGiveaway.recipient_email}
        </strong>
        &nbsp;has expired. Please choose another friend
        or family member to send your free box to.
        <br />
        Share-a-box credits are subject to expiration if not used within one-year.
      </p>
    );
  } else if (remainingGiveawaysCount === 0) {
    content = (
      <NotEnoughOrders ordersToNextGiveaway={ordersToNextGiveaway} />
    );
  } else {
    content = (
      <p className="u-text--md u-text--lg-lg">
        Know someone who&apos;d enjoy delicious plant-based meals
        delivered right to their door? Send &apos;em a free box and
        help us cultivate the plant-based revolution at the same time.
        <br />
        Share-a-box credits are subject to expiration if not used within one-year.
      </p>
    );
  }

  return (
    <div className="col-lg-10">
      <div className="c-giveaways-hero">
        <div className="c-giveaways-hero__content">
          <h1 className="c-heading c-heading--brand-4">
            {remainingGiveawaysCount === 0
              ? 'Spread a Little Plant-Based Love'
              : 'Send your friends a FREE Purple Carrot box!'}
          </h1>
        </div>
      </div>

      {showSuccessBanner && (
      // @ts-expect-error TODO: fix null safety
        <Banner.Success ref={bannerRef} isCentered>
          Invite sent successfully to &nbsp;
          <strong>
            {giftUserEmail}
          </strong>
        </Banner.Success>
      )}

      {showInfoBanner && (
      // @ts-expect-error TODO: fix null safety
        <Banner ref={bannerRef} isCentered>
          Unable to send the gift!
        </Banner>
      )}

      <div className="row">
        <div className="col-md-6 col-lg-7 order-1">
          <div className="u-text--md u-text--lg-lg">
            {content}
          </div>
        </div>

        <div
          className={[
            'col-md-5',
            'offset-md-1',
            'col-lg-4',
            'offset-lg-1',
            'order-3',
            'order-md-2',
            'order-lg-2',
          ].join(' ')}
        >
          <section className="c-giveaways-dashboard">
            <div>
              <DeliverIcon height="32" width="32" />
              <div className="c-heading c-heading--no-margin c-heading--brand-8">
                Free Boxes Available
              </div>
              <div className="c-heading c-heading--no-margin c-heading--brand-5">
                {remainingGiveawaysCount}
              </div>
            </div>
            <hr />
            <div>
              <EmailIcon height="32" width="32" />
              <div className="c-heading c-heading--no-margin c-heading--brand-8">
                Invites sent
              </div>
              <div className="c-heading c-heading--no-margin c-heading--brand-5">
                {submittedGiveawaysCount}
              </div>
            </div>
            <hr />
            <div>
              <SuccessIcon height="32" width="32" />
              <div className="c-heading c-heading--no-margin c-heading--brand-8">
                Invites redeemed
              </div>
              <div className="c-heading c-heading--no-margin c-heading--brand-5">
                {redeemedGiveawaysCount}
              </div>
            </div>
          </section>
        </div>

        {giveawayToSend !== null && (
          <div className="col-12 order-2 order-md-3 order-lg-3">
            <GiveawayForm
              showSuccessBanner={handleShowSuccessBanner}
                // @ts-expect-error TODO: fix null safety
              giveawayToSend={giveawayToSend}
              showInfobanner={handleShowInfoBanner}
            />
          </div>
        )}

        <div className="col-12 order-4">
          <>
            <div className="">
              <button
                type="button"
                tabIndex={0}
                className={[
                  'c-link',
                  'c-giveaways-toggle c-toggle',
                  showGiveawayList ? 'is-active' : '',
                ].join(' ')}
                onClick={() => setShowGiveawayList(!showGiveawayList)}
                onKeyPress={(e) => e.key === 'Enter'}
              >
                <span className={[
                  'c-toggle__content',
                  'mr-1',
                ].join(' ')}
                >
                  {showGiveawayList
                    ? 'Hide all invites sent'
                    : 'View all invites sent'}
                </span>
                <CaretIcon
                  className="c-svg-fill-primary-color c-svg-stroke-primary-color"
                  width="15"
                  height="15"
                  strokeWidth="1"
                />
              </button>
            </div>
          </>

          {showGiveawayList && (
            <div>
              <section id="giveaways" className="c-giveaways">
                <div className="c-giveaways__header">
                  <div>
                    <h3 className="c-heading c-heading--brand-5">Invites</h3>
                    <p className="c-giveaways__info u-text--sm">
                      (credit will expire 29 days after invite is sent)
                    </p>
                  </div>
                  <div className="c-giveaways__filter">
                    <div
                      className={[
                        'c-input-group',
                        'c-input-group--icon-right',
                        'align-items-center',
                      ].join(' ')}
                    >
                      <strong className="mr-1">View: </strong>
                      <select
                        id="cc-exp-year"
                        onChange={(e) => filterGiveaways(e.target.value)}
                      >
                        <option value="" hidden>All</option>
                        {statusOptions.map((val) => (
                          <option key={val.id} value={val.id}>
                            {capitalize(val.label)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="c-link c-input-icon c-input-icon--right"
                      >
                        <CaretIcon
                          className="c-select__caret-icon c-svg-fill-gray-115-color"
                          height="12"
                          width="12"
                        />
                      </button>
                    </div>
                  </div>

                </div>
                <hr />
                {sortedSentGiveaways.map((giveaway, i) => (
                  <div
                    className={[
                      'c-giveaway',
                      giveaway.beenRedeemed
                        ? 'c-giveaway--claimed'
                        : ['c-giveaway--', giveaway?.status].join(''),
                    ].join(' ')}
                    key={seed(i)}
                  >
                    <div className="c-giveaway__date">
                      <p>
                        {giveaway.sent_at
                          ? monthDayAndYear(new Date(giveaway.sent_at))
                          : 'Date N/A'}
                        &nbsp;&nbsp;
                      </p>
                    </div>
                    <div className="c-giveaway__flex">
                      <div className="c-giveaway__name">
                        <p>
                          &nbsp;&nbsp;
                          {giveaway.recipient_name}
                        </p>
                      </div>
                      <div className="c-giveaway__email">
                        <p>
                          {giveaway.recipient_email}
                        </p>
                      </div>
                      <div className="c-giveaway__action">
                        <p>
                          {statusName(giveaway)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GiveawayPage;
